import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import {
  watch,
  inject,
  provide,
  ref,
  onServerPrefetch,
  onMounted,
  computed,
  onUnmounted,
  nextTick,
} from 'vue';
import {
  getItemKey,
  getServerItemKey,
  subscribeKey,
  bind,
  invokeKey,
  loadingProgressKey
} from '@drapejs/core';
import {
  usePageView,
} from '@distancify-storefront/tracking-gtm';

import StandardLayout from './layouts/StandardLayout.vue';
import BlankLayout from './layouts/BlankLayout.vue';
import CountrySelectorLayout from './layouts/CountrySelectorLayout.vue';
import { emptyCart, setContactId, cart as voyadoCart } from '@/helpers/voyado-engage';
import {
  isSearchOpenKey,
  searchStringKey,
  searchResultKey,
  channelKey,
  countrySwitcherKey,
  cartKey,
  userKey,
  frameworkContentKey,
  litiumKey,
} from '@/keys';
import useContext from '@/composables/useContext';
import useModals from '@/composables/useModals';
import useCountrySwitch from '@/composables/useCountrySwitch';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { commands as voyadoCommands } from '@/commands/voyado';
import { buildQueryParams, getPrefferedCountryCookieValue } from '../utils';
import { sales } from '@distancify/drapejs-litium';


export default /*#__PURE__*/_defineComponent({
  __name: 'Layout',
  setup(__props) {

const { route, page, ssrContext } = useContext();
const { setCountry } = useCountrySwitch();
const getServerItem = inject(getServerItemKey);
const getItem = inject(getItemKey);
const subscribe = inject(subscribeKey);
const invoke = inject(invokeKey);
const loadingProgress = inject(loadingProgressKey, ref(null));

if (route.query.country) {
  onServerPrefetch(async () => {
    await setDesiredCountry();
  });
}

const cart = ref();
provide(cartKey, cart);

const channel = bind('__channel');
provide(channelKey, channel);

const { websiteText } = useWebsiteTexts();

watch(() => channel.value?.systemId, async (val) => {
  await setChannelInGTM();
}, { immediate: true });

watch(loadingProgress, async () => {
  if (typeof loadingProgress.value === 'number') {
    return;
  }

  await setChannelInGTM();

  nextTick(async () => {
    await setChannelInGTM();
  });
});

const frameworkContent = computed(
  () => channel.value?.frameworkContentJson || {}
);
provide(frameworkContentKey, frameworkContent);

const countrySwitcher = ref(getServerItem('__countrySwitcher'));
provide(countrySwitcherKey, countrySwitcher);

const user: any = ref({
  isAuthenticated: false,
});
provide(userKey, user);

const litium = ref();
provide(litiumKey, litium);

const isSearchOpen = ref(false);
provide(isSearchOpenKey, isSearchOpen);

const searchString = ref('');
provide(searchStringKey, searchString);

const searchResult = ref(null);
provide(searchResultKey, searchResult);

provide(
  '$metaProductReactive',
  computed(() => {
    const fieldsJson = channel.value?.metaProduct?.fieldsJson;

    return {
      fieldsJson,
    };
  })
);
provide(
  '$mainMenuReactive',
  computed(() => channel.value?.headerMenu || [])
);
provide(
  '$footerLinksReactive',
  computed(() => channel.value?.footerLinks || [])
);


usePageView();

function extractCartFromRestoreParam(cartRestore: string) {
  const parts = cartRestore.split('|');
  const cartRef = parts[0];

  const items = [] as { articleNumber: string; quantity: number }[];
  for (let i = 1; i < parts.length; i++) {
    const [articleNumber, quantity] = parts[i].split(':');
    items.push({ articleNumber, quantity: parseInt(quantity, 10) });
  }

  return {
    cartRef,
    items,
  };
}

onMounted(async () => {
  const userCountry = getPrefferedCountryCookieValue() || '';
  await setCountry(userCountry);

  subscribe(
    '__cart',
    (data) => {
      cart.value = data;
    },
    { immediate: true }
  );

  subscribe(
    '__user',
    (data) => {
      if (data) {
        user.value = data;
      }
    },
    { immediate: true }
  );

  subscribe(
    '__litium',
    (data) => {
      litium.value = data;
    },
    { immediate: true }
  );

  const currentLitium = await getItem('__litium');
  const userValue = await getItem('__user');
  if (userValue) {
    user.value = userValue;
  }

  user.value.isAuthenticated =
    currentLitium?.uid && currentLitium?.uid !== 'NotAuthenticated';

  cart.value = await getItem('__cart');

  const { eclub, cartRestore } = route.query;
  if (eclub) {
    invoke!(voyadoCommands.getContactId, { eclub, url: route.href }).then((result) => {
        if (result?.contactId) {
            setContactId(result.contactId);
        }
    });
  }

  if (cartRestore) {
    try {
        const cartRestoreData = extractCartFromRestoreParam(cartRestore);
        if (!cartRestoreData.cartRef) throw new Error('No cart ref in cart restore data');
        
        invoke!(voyadoCommands.setCartRef, { cartRef: cartRestoreData.cartRef, url: `${route.protocol}//${route.host}${route.pathname}` });

        for(const item of cartRestoreData.items) {
          const missingQuantity = item.quantity - (cart.value?.rows.find(row => row.articleNumber === item.articleNumber)?.quantity || 0);
          if (missingQuantity > 0) {
            invoke?.(
              sales.addToCart,
              {
                articleNumber: item.articleNumber,
                quantity: missingQuantity,
                url: `${route.protocol}//${route.host}${route.pathname}`,
              });
          }
        }
    } catch (e) {
      console.error('Failed to restore cart', e);
    }
  }

  watch(cart, (newCart, oldCart) => {
    if (JSON.stringify(newCart) !== JSON.stringify(oldCart)) {
      if (oldCart?.voyadoCartRef && newCart.voyadoCartRef !== oldCart.voyadoCartRef) {
        emptyCart(oldCart);
      }

      voyadoCart(newCart, channel.value?.locale, channel.value?.rootPath);
    }
  }, { deep: true });

  subscribe(
    '__countrySwitcher',
    (val) => {
      countrySwitcher.value = val;
    },
    { immediate: true }
  );
  if (!countrySwitcher.value) {
    countrySwitcher.value = await getItem('__countrySwitcher');
  }

  window.addEventListener('resize', updateVH);
  updateVH();
});

onUnmounted(() => {
  window.removeEventListener('resize', updateVH);
});

function updateVH() {
  document.documentElement.style.setProperty(
    '--vh',
    `${window.innerHeight * 0.01}px`
  );
}

useModals();

const layout = computed(() => {
  if (page.value?.type === 'ComparePrint') {
    return BlankLayout;
  }

  if (page.value?.type === 'Media') {
    return BlankLayout;
  }

  if (page.value?.type === 'Login') {
    return BlankLayout;
  }

  if ((page.value?.type || page.value?.http_status === 404) && channel.value?.id !== 'ChannelSelector') {
    return StandardLayout;
  }

  return CountrySelectorLayout;
});

async function setDesiredCountry() {
  try {
    const { country } = route.query;
    await setCountry(country);
  } finally {
    const query = { ...route.query };
    delete query.country;
    let params = buildQueryParams(query) || '';
    if (params) {
      params = `?${params}`;
    }

    let host = ssrContext.req.hostname;
    if (host === 'localhost') {
      host += `:${ssrContext.req.host.split(':')[1]}`;
    }

    const url = `//${host}${route.pathname}${params}`;
    ssrContext.redirectUrl = url;
  }
}

async function setChannelInGTM() {
  if (typeof window !== 'undefined' && typeof window.dataLayer !== 'undefined') {
    const currentLitium = await getItem("__litium");
    if (!channel?.value?.systemId) return;

    window.dataLayer.push({
      channel: channel.value?.systemId,
      sessionId: currentLitium?.session
    });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(layout))))
}
}

})